import { timestampToDate } from "@storytell/cp-client";
import type { Message } from "@storytell/cp-client/src/gen/chat/v3/chat_messages_pb";
import { produce } from "solid-js/store";
import type {
  SetStreamingStore,
  StreamingBlockId,
  StreamingMessageId,
  StreamingOperation,
  StreamingOperationId,
  StreamingStore,
} from "../streamingTypes";

const OPERATION_INDEX_OFFSET = -1;

// Blocks whose start arrived before their operation's, for the operation to adopt when it starts.
// An operation that never starts leaves its ids here, as its blocks stay in the store the same way.
const orphanedBlocks = new Map<StreamingOperationId, StreamingBlockId[]>();

export const recordOrphanedBlock = (operationId: StreamingOperationId, blockId: StreamingBlockId) => {
  const blocks = orphanedBlocks.get(operationId);
  if (blocks) blocks.push(blockId);
  else orphanedBlocks.set(operationId, [blockId]);
};

/**
 * The blocks an operation owns as it starts: those that started before it, then those linked to the
 * operation it replaces, since playback and a recording loaded over a live turn restart every
 * operation. Not a walk of every block in the store: loading a chat replays every operation start,
 * so that walk makes the load quadratic in the chat's size.
 */
const adoptBlocks = (store: StreamingStore, operationId: StreamingOperationId): StreamingBlockId[] => {
  const adopted: StreamingBlockId[] = [];
  for (const blockId of [
    ...(orphanedBlocks.get(operationId) ?? []),
    ...(store.operations[operationId]?.blocks ?? []),
  ]) {
    if (store.blocks[blockId]?.operationId === operationId && !adopted.includes(blockId)) adopted.push(blockId);
  }
  orphanedBlocks.delete(operationId);
  return adopted;
};

/**
 * Links a newly stored operation to the message it belongs to.
 *
 * Operation events can reach the store before the MESSAGE_START that declares their message, so
 * the message may not exist yet. In that case the operation is recorded as orphaned and
 * processMessageStart (or recording replay) adopts it when the message lands. Skipping that
 * record leaves the operation in the store attached to nothing, and it renders nowhere.
 *
 * Pass `insertAt` when the event carries the operation's position in the message; otherwise the
 * operation is appended.
 */
export const attachOperationToMessage = (
  store: StreamingStore,
  messageId: StreamingMessageId,
  operationId: StreamingOperationId,
  insertAt?: number
) => {
  const message = store.messages[messageId];

  if (!message) {
    if (!store.orphanedOperations[messageId]) {
      store.orphanedOperations[messageId] = [];
    }
    if (!store.orphanedOperations[messageId].includes(operationId)) {
      store.orphanedOperations[messageId].push(operationId);
    }
    return;
  }

  if (message.operations.includes(operationId)) return;

  if (insertAt === undefined) {
    message.operations.push(operationId);
  } else {
    message.operations.splice(insertAt, 0, operationId);
  }
};

export const processOperationStart = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }
  const {
    id,
    index: _index,
    role,
    operationId: _operationId,
    data: { value: data },
  } = message;

  const index = _index + OPERATION_INDEX_OFFSET;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  const streamingOperation: StreamingOperation = {
    index,
    operationId,
    isComplete: false,
    messageId,
    blocks: [],
    name: data.operationName,
    activity: data.activity,
    startedAt: data.startedAt ? timestampToDate(data.startedAt) : undefined,
    role: role,
    status: data.status,
    completedAt: data.completedAt ? timestampToDate(data.completedAt) : undefined,
    task: undefined,
    progressMessage: data.progressMessage || undefined,
    parentOperationId: (data.parentOperationId as StreamingOperationId) || undefined,
  };

  setStore(
    produce((store) => {
      attachOperationToMessage(store, messageId, operationId, index);

      streamingOperation.blocks = adoptBlocks(store, operationId);
      store.operations[operationId] = streamingOperation;
    })
  );
};

export const processOperationProgress = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }

  const {
    operationId: _operationId,
    data: { value: data },
  } = message;
  const operationId = _operationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const operation = store.operations[operationId];
      if (!operation) {
        console.warn(`Progress for operation ${operationId} arrived before start, skipping`);
        return;
      }

      if (data.operationName !== operation.name) {
        operation.name = data.operationName;
      }
      if (data.activity !== operation.activity) {
        operation.activity = data.activity;
      }
      if (data.progressMessage !== operation.progressMessage) {
        operation.progressMessage = data.progressMessage || undefined;
      }
      if (data.status !== operation.status) {
        operation.status = data.status;
      }
      if (data.parentOperationId && !operation.parentOperationId) {
        operation.parentOperationId = data.parentOperationId as StreamingOperationId;
      }
    })
  );
};

export const processOperationComplete = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }

  const {
    operationId: _operationId,
    data: { value: data },
  } = message;
  const operationId = _operationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const operation = store.operations[operationId];
      if (!operation) {
        console.warn(`Complete for operation ${operationId} arrived before start, skipping`);
        return;
      }

      operation.isComplete = true;
      if (data.operationName !== operation.name) {
        operation.name = data.operationName;
      }
      if (data.activity !== operation.activity) {
        operation.activity = data.activity;
      }
      if (data.startedAt !== operation.startedAt) {
        operation.startedAt = data.startedAt ? timestampToDate(data.startedAt) : undefined;
      }
      if (data.status !== operation.status) {
        operation.status = data.status;
      }
      if (data.completedAt !== operation.completedAt) {
        operation.completedAt = data.completedAt ? timestampToDate(data.completedAt) : undefined;
      }
      if (data.progressMessage && data.progressMessage !== operation.progressMessage) {
        operation.progressMessage = data.progressMessage;
      }
      if (data.parentOperationId && !operation.parentOperationId) {
        operation.parentOperationId = data.parentOperationId as StreamingOperationId;
      }

      // for (const blockId of operation.blocks) {
      //   const block = store.blocks[blockId];
      //   if (block) {
      //     block.isComplete = true;
      //   }
      // }
    })
  );
};

export const processOperationFailed = (setStore: SetStreamingStore, message: Message) => {
  if (message.data.case !== "messageOperationStatusData") {
    throw new Error(`Expected messageOperationStatusData, got ${message.data.case}`);
  }

  const {
    id,
    index: _index,
    role,
    operationId: _operationId,
    data: { value: data },
  } = message;

  const index = _index + OPERATION_INDEX_OFFSET;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  setStore(
    produce((store) => {
      let operation = store.operations[operationId];

      if (!operation) {
        // Create the operation if it doesn't exist (early failure case)
        const streamingOperation: StreamingOperation = {
          index,
          operationId,
          isComplete: true,
          messageId,
          blocks: adoptBlocks(store, operationId),
          name: data.operationName,
          activity: data.activity,
          startedAt: data.startedAt ? timestampToDate(data.startedAt) : undefined,
          role: role,
          status: data.status,
          completedAt: data.completedAt ? timestampToDate(data.completedAt) : undefined,
          task: undefined,
          progressMessage: data.progressMessage || undefined,
          parentOperationId: (data.parentOperationId as StreamingOperationId) || undefined,
        };

        store.operations[operationId] = streamingOperation;
        operation = streamingOperation;

        // Add to message's operation list
        attachOperationToMessage(store, messageId, operationId, index);
      } else {
        // Update existing operation to failed state
        operation.isComplete = true;
        operation.status = data.status;
        operation.completedAt = data.completedAt ? timestampToDate(data.completedAt) : undefined;
        if (data.operationName !== operation.name) {
          operation.name = data.operationName;
        }
        if (data.activity !== operation.activity) {
          operation.activity = data.activity;
        }
        if (data.progressMessage && data.progressMessage !== operation.progressMessage) {
          operation.progressMessage = data.progressMessage;
        }
        if (data.parentOperationId && !operation.parentOperationId) {
          operation.parentOperationId = data.parentOperationId as StreamingOperationId;
        }
      }
    })
  );
};
